// Dimension scoring for the TrueSpan pie chart.
//
// Each wellness dimension maps to a CareDaily category root (e.g. "dow.physical").
// A single facility-level list of sub-metrics (e.g. "resident_activity",
// "wellness_attendance") is applied to every dimension. For each sub-metric, the
// admin picks how to turn the reading into a 0–100% figure (the `denom` mode):
//
//   'apiMax' — value / max_value from trends_metadata        (default)
//   'fixed'  — value / a fixed max entered in config
//   'share'  — value / Σ(value across all dimension categories)   [no max needed]
//
// The dimension score is the weighted mean of its sub-metric percentages, then
// renormalized across dimensions into the pie's relative shares (in Dashboard.tsx).
//
// Readings come from the last 30 days of the trends time series (utils/wheelTrends),
// or trends_recently (7 days, excluding today) as a fallback. Both carry `avg`.

export type DenomMode = 'apiMax' | 'fixed' | 'share';

export interface SubMetricConfig {
  /** Metric suffix after the category root, e.g. "resident_activity". */
  name: string;
  /** Reading field for the numerator (and the share denominator). Default avg ?? value. */
  valueKey?: string | null;
  /** Relative weight in the dimension mean. Default 1. Explicit 0 drops the sub-metric. */
  weight?: number;
  /** How to scale the reading to 0–100%. Default 'share'. */
  denom?: DenomMode;
  /** Fixed denominator used when denom === 'fixed'. */
  fixedMax?: number;
}

type ReadingMap = Record<string, any>;  // recentTrends[key] = { avg, std, n, ... }
type MetadataMap = Record<string, any>; // metadata[key]     = { min_value, max_value, ... }

const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));

/** Read a reading field, or null if it isn't a finite number. */
function readValue(reading: any, valueKey?: string | null): number | null {
  if (!reading) return null;
  const v = valueKey ? reading[valueKey] : (reading.avg ?? reading.value);
  return typeof v === 'number' && isFinite(v) ? v : null;
}

/** Turn one sub-metric reading into a 0–100% figure per its denominator mode, or null to skip. */
function subMetricPercent(
  sm: SubMetricConfig,
  categoryRoot: string,
  allCategoryRoots: string[],
  recentTrends: ReadingMap,
  metadata: MetadataMap
): number | null {
  const fullKey = `${categoryRoot}.${sm.name}`;
  const value = readValue(recentTrends?.[fullKey], sm.valueKey);
  if (value === null) return null;

  const mode: DenomMode = sm.denom ?? 'share';

  if (mode === 'share') {
    let total = 0;
    let any = false;
    for (const root of allCategoryRoots) {
      const v = readValue(recentTrends?.[`${root}.${sm.name}`], sm.valueKey);
      if (v !== null) { total += v; any = true; }
    }
    if (!any || total <= 0) return null;
    return clamp(value / total, 0, 1) * 100;
  }

  if (mode === 'apiMax') {
    const meta = metadata?.[fullKey];
    const max = typeof meta?.max_value === 'number' ? meta.max_value : null;
    const min = typeof meta?.min_value === 'number' ? meta.min_value : 0;
    if (max === null || max <= min) return null;
    return clamp((value - min) / (max - min), 0, 1) * 100;
  }

  // fixed
  const max = sm.fixedMax;
  if (typeof max !== 'number' || max <= 0) return null;
  return clamp(value / max, 0, 1) * 100;
}

/**
 * Score a dimension as the weighted mean of its sub-metric percentages.
 *
 * With no sub-metric list configured, falls back to the legacy raw-reading score of
 * the dimension's own category key (renormalized across dimensions by the caller).
 *
 * @param categoryRoot      this dimension's category, e.g. "dow.physical"
 * @param valueKey          legacy reading field for the fallback path (avg ?? value when null)
 * @param subMetrics        facility-level sub-metric list (applied to every dimension)
 * @param allCategoryRoots  category roots of all configured dimensions (the 'share' denominator set)
 * @param recentTrends      CareDaily readings keyed by full metric key
 * @param metadata          trends_metadata keyed by full metric key (for the 'apiMax' mode)
 *
 * Returns null when the dimension has no usable readings — callers must treat null as
 * "no data yet", not as zero.
 */
export function computeDimensionScore(
  categoryRoot: string | undefined,
  valueKey: string | null | undefined,
  subMetrics: SubMetricConfig[] | undefined,
  allCategoryRoots: string[],
  recentTrends: ReadingMap,
  metadata: MetadataMap = {}
): number | null {
  if (!categoryRoot) return null;

  // Backward compatible: with no facility-level sub-metric list, score on the raw
  // reading of the dimension's own category key (legacy behavior, renormalized later).
  if (!subMetrics || subMetrics.length === 0) {
    return readValue(recentTrends?.[categoryRoot], valueKey);
  }

  let weightSum = 0;
  let weightedPctSum = 0;

  for (const sm of subMetrics) {
    if (!sm.name) continue;
    const pct = subMetricPercent(sm, categoryRoot, allCategoryRoots, recentTrends, metadata);
    if (pct === null) continue;
    const w = typeof sm.weight === 'number' && sm.weight >= 0 ? sm.weight : 1;
    weightSum += w;
    weightedPctSum += pct * w;
  }

  return weightSum > 0 ? weightedPctSum / weightSum : null;
}
